import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = ["Aadhaar Card", "PAN Card", "Bank Passbook", "KSEB Bill",
  "Electricity Bill", "Agreement", "Quotation", "Net Meter Doc",
  "Warranty Certificate", "Completion Report", "Other"];

export default function Documents({ ownerType, ownerId }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("Aadhaar Card");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get(`/documents/linked?owner_type=${ownerType}&owner_id=${ownerId}`).then((r) => setDocs(r.data));

  useEffect(() => { if (ownerId) load(); }, [ownerType, ownerId]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("owner_type", ownerType);
    fd.append("owner_id", ownerId);
    fd.append("doc_type", docType);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType} uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Removed");
    load();
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div data-testid="documents-block">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Documents</div>
        <div className="flex gap-2 items-center">
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="rounded-none h-9 w-44 text-xs" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={fileRef} type="file" onChange={handleFile} className="hidden" data-testid="doc-file-input" />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-9"
            data-testid="doc-upload-button"
          >
            <Upload className="w-3 h-3 mr-2" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="text-xs text-[#9CA3AF] italic py-6 text-center border border-dashed border-[#E5E7EB]">
          No documents uploaded yet.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between px-3 py-2.5" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{d.filename}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
                    {d.doc_type} · {fmtSize(d.size)} · {fmtDate(d.created_at)}
                    {d.owner_type !== ownerType && (
                      <span className="ml-1 px-1 bg-[#FEF3C7] text-[#92400E]">
                        from {d.owner_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button onClick={() => download(d)} variant="ghost" size="sm" className="rounded-none h-8 text-[10px] uppercase tracking-widest font-bold" data-testid={`doc-download-${d.id}`}>
                  <Download className="w-3 h-3" />
                </Button>
                <Button onClick={() => remove(d.id)} variant="ghost" size="sm" className="rounded-none h-8 text-[10px] uppercase tracking-widest font-bold text-[#EF4444] hover:text-[#EF4444]" data-testid={`doc-delete-${d.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
